// src/watchface/physicsBalls.ts
// Absorbed from watchfaces/physics-balls.js (Task 35). This file is
// vendored-style code calling into Matter.js (a typeless external) and
// WebGL — strict-null checks produce many false positives here. Proper
// typing is a follow-up task; for now @ts-nocheck keeps the build green
// without regressing runtime behavior.
// @ts-nocheck
/* eslint-disable */

import '../libs/matter';
import { WatchfaceBase } from './base';
import { loopQuatToArray, quatToDeviceRotation } from '../utils/quaternion';
import { loop, hasSystemFreeRotate } from '../loopSdk';

/** Minimal shape of a Matter.js physics engine used by PhysicsBallsFace. */
interface MatterEngineRef {
  world: unknown;
  gravity: { x: number; y: number };
}

/** Minimal shape of a Matter.js body used by PhysicsBallsFace. */
interface MatterBodyRef {
  position: { x: number; y: number };
  circleRadius: number;
  label: string;
  _lineLength?: number;
  _lineThickness?: number;
}

/** Parsed segment shape used internally for collider building. */
interface ParsedSegment {
  cx: number;
  cy: number;
  length: number;
  angle: number;
}

export class PhysicsBallsFace extends WatchfaceBase {
    _canvas: HTMLCanvasElement | null;
    _ctx: CanvasRenderingContext2D | null;
    _engine: MatterEngineRef | null;
    _balls: MatterBodyRef[];
    _boundaryBodies: MatterBodyRef[];
    _hourHandBody: MatterBodyRef | null;
    _minuteHandBody: MatterBodyRef | null;
    _secondHandBody: MatterBodyRef | null;
    _lockLineBodies: MatterBodyRef[];
    _lastSpawnedSecond: number;
    _width: number;
    _height: number;
    _cx: number;
    _cy: number;
    _radius: number;
    _boundaryRadius: number;
    _scale: number;
    _parallaxX: number;
    _parallaxY: number;
    _gravityX: number;
    _gravityY: number;
    _ballColors: string[];
    _minimalProgress: number;
    _dpr: number;
    _config: {
        gravity: number;
        ballSize: number;
        restitution: number;
        handWidth: number;
    };
    _chronoActive: boolean;
    _chronoStartTime: number;
    _chronoDurationMs: number;
    _chronoSnapHour: number;
    _chronoSnapMinute: number;
    _chronoSnapSecond: number;
    _chronoOnMidpoint: (() => void) | null;
    _chronoPhase: string | null;
    _chronoMidpointFired: boolean;
    _chronoInStartTime: number;

    constructor(container: HTMLElement, options?: Record<string, unknown>) {
        super(container, options);
        this._canvas = null;
        this._ctx = null;
        this._engine = null;
        this._balls = [];
        this._boundaryBodies = [];
        this._hourHandBody = null;
        this._minuteHandBody = null;
        this._secondHandBody = null;
        this._lockLineBodies = [];
        this._lastSpawnedSecond = -1;
        this._width = 800;
        this._height = 800;
        this._cx = 400;
        this._cy = 400;
        this._radius = 380;
        this._boundaryRadius = 370;
        this._scale = 1;
        this._parallaxX = 0;
        this._parallaxY = 0;
        this._gravityX = 0;
        this._gravityY = 1;
        this._ballColors = ['#6489F4', '#00ED95', '#8D51CB', '#E4ABB2', '#FFC800'];
        this._minimalProgress = 0;
        this._dpr = 1;

        this._config = {
            gravity: 1,
            ballSize: 45,
            restitution: 0.6,
            handWidth: 36,
        };

        // Chrono state
        this._chronoActive = false;
        this._chronoStartTime = 0;
        this._chronoDurationMs = 1300;
        this._chronoSnapHour = 0;
        this._chronoSnapMinute = 0;
        this._chronoSnapSecond = 0;
        this._chronoOnMidpoint = null;
        this._chronoPhase = null;
        this._chronoMidpointFired = false;
        this._chronoInStartTime = 0;
    }

    // Scale absolute pixel values from 800px design space to actual container
    _s(px: number): number { return px * this._scale; }

    override mount(): void {
        this._width = this.container.clientWidth || 800;
        this._height = this.container.clientHeight || 800;
        this._cx = this._width / 2;
        this._cy = this._height / 2;
        this._radius = Math.min(this._width, this._height) / 2;
        this._scale = Math.min(this._width, this._height) / 800;
        const wallThickness = this._s(50);
        this._boundaryRadius = this._radius - this._s(10) + wallThickness / 2;

        // Create canvas (DPR-aware for crisp rendering, capped at 2 for low-end devices)
        this._dpr = Math.min(window.devicePixelRatio || 1, 2);
        this._canvas = document.createElement('canvas');
        this._canvas.width = this._width * this._dpr;
        this._canvas.height = this._height * this._dpr;
        this._canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
        this.container.appendChild(this._canvas);
        this._ctx = this._canvas.getContext('2d');
        this._ctx!.scale(this._dpr, this._dpr);

        // Init Matter.js
        this._engine = Matter.Engine.create();
        this._engine.gravity.x = 0;
        this._engine.gravity.y = this._config.gravity;

        // Circular boundary — thick segments, no gaps
        this._boundaryBodies = [];
        const segments = 60;
        for (let i = 0; i < segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const nextAngle = ((i + 1) / segments) * Math.PI * 2;
            const midAngle = (angle + nextAngle) / 2;
            const segLength = 2 * this._boundaryRadius * Math.sin(Math.PI / segments) * 1.15;
            const body = Matter.Bodies.rectangle(
                this._cx + Math.cos(midAngle) * this._boundaryRadius,
                this._cy + Math.sin(midAngle) * this._boundaryRadius,
                segLength, this._s(50),
                { isStatic: true, angle: midAngle, friction: 0.05, restitution: 0.5 }
            );
            this._boundaryBodies.push(body);
        }
        Matter.Composite.add(this._engine.world, this._boundaryBodies);

        // Hand colliders
        const handColliderThickness = this._s(18);
        const hourLen = this._radius * 0.33;
        const minuteLen = this._radius * 0.495;
        const secondLen = this._radius * 0.81;
        this._hourHandBody = Matter.Bodies.rectangle(
            this._cx, this._cy - hourLen * 0.5,
            hourLen, handColliderThickness,
            { isStatic: true, friction: 0.1, restitution: 0.3 }
        );
        this._minuteHandBody = Matter.Bodies.rectangle(
            this._cx, this._cy - minuteLen * 0.5,
            minuteLen, handColliderThickness,
            { isStatic: true, friction: 0.1, restitution: 0.3 }
        );
        this._secondHandBody = Matter.Bodies.rectangle(
            this._cx, this._cy - secondLen * 0.5,
            secondLen, handColliderThickness,
            { isStatic: true, friction: 0.1, restitution: 0.3 }
        );
        Matter.Composite.add(this._engine.world, [
            this._hourHandBody,
            this._minuteHandBody,
            this._secondHandBody,
        ]);

        this._balls = [];
        this._lastSpawnedSecond = -1;

        // Spawn balls for elapsed seconds in current minute
        this._spawnInitialBalls();
    }

    _spawnInitialBalls(): void {
        if (!this._engine) return;
        const now = new Date();
        const currentSecond = now.getSeconds();
        if (currentSecond === 0) return;

        for (let s = 0; s < currentSecond; s++) {
            // Place balls randomly within the lower half of the boundary
            const angle = Math.random() * Math.PI * 2;
            const dist = Math.random() * this._boundaryRadius * 0.7;
            const x = this._cx + Math.cos(angle) * dist;
            const y = this._cy + Math.sin(angle) * dist;
            const radius = this._s(20 + Math.random() * (this._config.ballSize - 20));
            const color = this._ballColors[Math.floor(Math.random() * this._ballColors.length)];
            const r = Math.max(this._s(4), radius);
            const ball = Matter.Bodies.circle(x, y, r, {
                restitution: this._config.restitution,
                friction: 0.02,
                frictionAir: 0.005,
                density: 0.001,
                label: color,
            });
            Matter.Composite.add(this._engine.world, ball);
            this._balls.push(ball);
        }
        this._lastSpawnedSecond = currentSecond - 1;
    }

    override unmount(): void {
        if (this._engine) {
            this._clearLockLineColliders();
            Matter.Engine.clear(this._engine);
            this._engine = null;
        }
        if (this._canvas && this._canvas.parentNode) {
            this._canvas.parentNode.removeChild(this._canvas);
        }
        this._canvas = null;
        this._ctx = null;
        this._balls = [];
        this._boundaryBodies = [];
        this._hourHandBody = null;
        this._minuteHandBody = null;
        this._secondHandBody = null;
        this._lockLineBodies = [];
        this._chronoActive = false;
        this._chronoPhase = null;
    }

    override update(time: ClockPayload): void {
        if (!this._engine || !this._ctx) return;

        const frameNow = performance.now();
        let hourDeg = time.hourDeg;
        let minuteDeg = time.minuteDeg;
        let secondDeg = time.secondDeg;

        // Chrono animation
        if (this._chronoActive) {
            if (this._chronoPhase === 'out') {
                const elapsed = frameNow - this._chronoStartTime;
                const progress = Math.min(elapsed / this._chronoDurationMs, 1);

                if (progress >= 0.35 && !this._chronoMidpointFired) {
                    this._chronoMidpointFired = true;
                    if (typeof this._chronoOnMidpoint === 'function') {
                        this._chronoOnMidpoint();
                        return;
                    }
                }

                if (progress >= 1) {
                    this._chronoActive = false;
                    this._chronoPhase = null;
                } else {
                    hourDeg = this._getChronoHandAngle(progress, this._chronoSnapHour, time.hourDeg);
                    minuteDeg = this._getChronoHandAngle(progress, this._chronoSnapMinute, time.minuteDeg);
                    secondDeg = this._getChronoHandAngle(progress, this._chronoSnapSecond, time.secondDeg);
                }
            } else if (this._chronoPhase === 'in') {
                const elapsed = frameNow - this._chronoInStartTime;
                const inDuration = this._chronoDurationMs * 0.3;
                const localProgress = Math.min(elapsed / inDuration, 1);
                const progress = 0.7 + localProgress * 0.3;

                if (localProgress >= 1) {
                    this._chronoActive = false;
                    this._chronoPhase = null;
                } else {
                    hourDeg = this._getChronoHandAngle(progress, 0, time.hourDeg);
                    minuteDeg = this._getChronoHandAngle(progress, 0, time.minuteDeg);
                    secondDeg = this._getChronoHandAngle(progress, 0, time.secondDeg);
                }
            }
        }

        // Keep colliders aligned with the rendered hand angles
        this._syncHandCollider(this._hourHandBody, hourDeg, this._radius * 0.33);
        this._syncHandCollider(this._minuteHandBody, minuteDeg, this._radius * 0.495);
        this._syncHandCollider(this._secondHandBody, secondDeg, this._radius * 0.81);

        const mp = this._minimalProgress;

        // Spawn logic & physics (skip when fully minimal)
        if (mp < 1) {
            if (!this._chronoActive) {
                const currentSecond = Math.floor(time.seconds);
                if (currentSecond !== this._lastSpawnedSecond) {
                    // Minute reset
                    if (currentSecond === 0 && this._balls.length > 0) {
                        for (const ball of this._balls) {
                            Matter.Composite.remove(this._engine.world, ball);
                        }
                        this._balls = [];
                    }
                    // Spawn ball at 12 o'clock position, 20% below the top
                    const tipX = this._cx;
                    const tipY = this._radius * 0.4;
                    const radius = this._s(20 + Math.random() * (this._config.ballSize - 20));
                    const color = this._ballColors[Math.floor(Math.random() * this._ballColors.length)];
                    const r = Math.max(this._s(4), radius);
                    const ball = Matter.Bodies.circle(tipX, tipY, r, {
                        restitution: this._config.restitution,
                        friction: 0.02,
                        frictionAir: 0.005,
                        density: 0.001,
                        label: color,
                    });
                    Matter.Composite.add(this._engine.world, ball);
                    this._balls.push(ball);
                    this._lastSpawnedSecond = currentSecond;
                }
            }

            // Lerp gravity toward IMU target
            const lerpFactor = 0.35;
            this._engine.gravity.x += (this._gravityX - this._engine.gravity.x) * lerpFactor;
            this._engine.gravity.y += (this._gravityY - this._engine.gravity.y) * lerpFactor;

            // Step physics
            Matter.Engine.update(this._engine, 1000 / 60);
        }

        // Render
        const ctx = this._ctx;
        ctx.clearRect(0, 0, this._canvas!.width, this._canvas!.height);

        // Draw balls (fade with minimal progress)
        if (mp < 1) {
            ctx.globalAlpha = 1 - mp;
            for (const ball of this._balls) {
                ctx.beginPath();
                ctx.arc(ball.position.x, ball.position.y, ball.circleRadius, 0, Math.PI * 2);
                ctx.fillStyle = ball.label;
                ctx.fill();
            }
            ctx.globalAlpha = 1;
        }

        const hw = this._s(this._config.handWidth);

        // Draw second hand FIRST so it renders underneath the hour and
        // minute hands in lock state (and disappears in minimal/home mode).
        if (mp < 1) {
            ctx.globalAlpha = 1 - mp;
            this._drawHand(ctx, secondDeg, this._radius * 0.81, hw * 0.6, '#E2E2E2');
            ctx.globalAlpha = 1;
        }

        // Draw hour hand (always visible)
        this._drawHand(ctx, hourDeg, this._radius * 0.33, hw, '#FF3A5E', true);
        // Draw minute hand (always visible)
        this._drawHand(ctx, minuteDeg, this._radius * 0.495, hw, '#FF3A5E', true);

        // Center pivot — always visible, in both lock and home (minimal)
        // mode. It anchors the hour/minute hands visually; without it the
        // two hands look detached at the centre when minimal mode hides
        // the second hand and balls.
        ctx.beginPath();
        ctx.arc(this._cx, this._cy, hw * 0.7, 0, Math.PI * 2);
        ctx.fillStyle = '#FF3A5E';
        ctx.fill();
    }

    override setMinimalMode(p: number): void {
        this._minimalProgress = p;
    }

    _drawHand(ctx: CanvasRenderingContext2D, angleDeg: number, length: number, width: number, color: string, shadow = false): void {
        const rad = (angleDeg - 90) * Math.PI / 180;
        const tipX = this._cx + Math.cos(rad) * length;
        const tipY = this._cy + Math.sin(rad) * length;
        ctx.save();
        if (shadow) {
            ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
            ctx.shadowBlur = this._s(6);
            ctx.shadowOffsetX = this._s(2);
            ctx.shadowOffsetY = this._s(3);
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(this._cx, this._cy);
        ctx.lineTo(tipX, tipY);
        ctx.stroke();
        ctx.restore();
    }

    _syncHandCollider(body: MatterBodyRef | null, angleDeg: number, handLength: number): void {
        if (!body) return;
        const rad = (angleDeg - 90) * Math.PI / 180;
        const hx = this._cx + Math.cos(rad) * handLength * 0.5;
        const hy = this._cy + Math.sin(rad) * handLength * 0.5;
        Matter.Body.setPosition(body, { x: hx, y: hy });
        Matter.Body.setAngle(body, rad);
    }

    _clearLockLineColliders(): void {
        if (!this._engine || this._lockLineBodies.length === 0) {
            this._lockLineBodies = [];
            return;
        }
        Matter.Composite.remove(this._engine.world, this._lockLineBodies);
        this._lockLineBodies = [];
    }

    _buildLockLineColliders(segments: ParsedSegment[], thickness: number): void {
        if (!this._engine) return;
        this._clearLockLineColliders();
        const bodies: MatterBodyRef[] = [];
        for (const seg of segments) {
            const body = Matter.Bodies.rectangle(
                seg.cx,
                seg.cy,
                seg.length,
                thickness,
                { isStatic: true, friction: 0.08, restitution: 0.25 }
            );
            Matter.Body.setAngle(body, seg.angle);
            body._lineLength = seg.length;
            body._lineThickness = thickness;
            bodies.push(body);
        }
        if (bodies.length > 0) {
            Matter.Composite.add(this._engine.world, bodies);
        }
        this._lockLineBodies = bodies;
    }

    override setLockLineColliders(segments: LockLineColliderSegment[], options: LockLineColliderOptions = {}): void {
        if (!this._engine) return;

        const enabled = options.enabled === true;
        if (!enabled || !Array.isArray(segments) || segments.length === 0) {
            this._clearLockLineColliders();
            return;
        }

        const thickness = Math.max(this._s(3), Number(options.thickness) || this._s(10));
        const parsed: ParsedSegment[] = [];
        for (const seg of segments) {
            if (!seg) continue;
            const x1 = Number(seg.x1);
            const y1 = Number(seg.y1);
            const x2 = Number(seg.x2);
            const y2 = Number(seg.y2);
            if (![x1, y1, x2, y2].every(Number.isFinite)) continue;
            const dx = x2 - x1;
            const dy = y2 - y1;
            const length = Math.hypot(dx, dy);
            if (length < 1) continue;
            parsed.push({
                cx: (x1 + x2) * 0.5,
                cy: (y1 + y2) * 0.5,
                length,
                angle: Math.atan2(dy, dx),
            });
        }

        if (parsed.length === 0) {
            this._clearLockLineColliders();
            return;
        }

        if (this._lockLineBodies.length !== parsed.length) {
            this._buildLockLineColliders(parsed, thickness);
            return;
        }

        let needsRebuild = false;
        for (let i = 0; i < parsed.length; i++) {
            const body = this._lockLineBodies[i];
            const seg = parsed[i];
            if (!body) {
                needsRebuild = true;
                break;
            }
            if (
                Math.abs((body._lineLength || 0) - seg.length) > 0.5 ||
                Math.abs((body._lineThickness || 0) - thickness) > 0.2
            ) {
                needsRebuild = true;
                break;
            }
        }

        if (needsRebuild) {
            this._buildLockLineColliders(parsed, thickness);
            return;
        }

        for (let i = 0; i < parsed.length; i++) {
            const body = this._lockLineBodies[i];
            const seg = parsed[i];
            Matter.Body.setPosition(body, { x: seg.cx, y: seg.cy });
            Matter.Body.setAngle(body, seg.angle);
        }
    }

    override setParallax(x: number, y: number, _source?: string): void {
        this._parallaxX = x;
        this._parallaxY = y;
    }

    override onMotion(data: unknown): void {
        if (!this._engine) return;
        const G = 9.81;
        const sensitivity = 3.0;
        const motionData = data as {
            smoothGravity: { x: number; y: number };
            orientation?: { w: number; x: number; y: number; z: number };
        };

        // Raw canvas-frame gravity from IMU (y matches device orientation; x inverted).
        let gx = -motionData.smoothGravity.x;
        let gy = motionData.smoothGravity.y;

        // When native free-rotate is on, it snaps the displayed image by 90°
        // multiples to keep the UI upright. Matter.js runs in the hardware
        // canvas (not the snapped display frame), so raw gravity sends balls
        // to a canvas position that ends up rotated off visual-bottom after
        // native's snap. Counter-rotate gravity by the same snap angle so
        // balls still pool at visual-bottom.
        const freeRotateOn =
            hasSystemFreeRotate && loop?.system?.isFreeRotateEnabled?.() === true;
        if (freeRotateOn && motionData.orientation) {
            const { roll } = quatToDeviceRotation(loopQuatToArray(motionData.orientation));
            const snap = Math.round(-roll / (Math.PI / 2)) * (Math.PI / 2);
            if (snap !== 0) {
                const c = Math.cos(snap);
                const s = Math.sin(snap);
                const nx = gx * c - gy * s;
                const ny = gx * s + gy * c;
                gx = nx;
                gy = ny;
            }
        }

        this._gravityX = (gx / G) * sensitivity * this._config.gravity;
        this._gravityY = (gy / G) * sensitivity * this._config.gravity;
    }

    _chronoSmooth(t: number): number {
        const eIn = 0.1;
        const eOut = 0.25;
        const v = 1 / (1 - (eIn + eOut) / 2);
        if (t < eIn) {
            const p = t / eIn;
            return 0.5 * v * eIn * p * p;
        } else if (t > 1 - eOut) {
            const p = (1 - t) / eOut;
            return 1 - 0.5 * v * eOut * p * p;
        }
        return 0.5 * v * eIn + v * (t - eIn);
    }

    _getChronoHandAngle(progress: number, snapAngle: number, realAngle: number): number {
        const baseRot = 720 * this._chronoSmooth(progress);
        let conv = Math.min(progress / 0.35, 1);
        conv = conv * conv * (3 - 2 * conv);
        let handOff = ((snapAngle % 360) + 360) % 360;
        if (handOff > 180) handOff -= 360;
        let sep = Math.max((progress - 0.7) / 0.3, 0);
        sep = sep * sep * (3 - 2 * sep);
        const realOff = ((realAngle % 360) + 360) % 360;
        const offset = handOff * (1 - conv) + realOff * sep;
        return baseRot + offset;
    }

    override chronoOut(onMidpoint: (() => void) | null): void {
        if (this._chronoActive) return;
        const now = new Date();
        const ms = now.getMilliseconds();
        const s = now.getSeconds() + ms / 1000;
        const m = now.getMinutes() + s / 60;
        const h = (now.getHours() % 12) + m / 60;
        this._chronoSnapHour = h * 30;
        this._chronoSnapMinute = m * 6;
        this._chronoSnapSecond = s * 6;
        this._chronoStartTime = performance.now();
        this._chronoActive = true;
        this._chronoOnMidpoint = onMidpoint;
        this._chronoPhase = 'out';
        this._chronoMidpointFired = false;

        // Radial impulse — swirl all balls toward center
        for (const ball of this._balls) {
            const dx = this._cx - ball.position.x;
            const dy = this._cy - ball.position.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            Matter.Body.applyForce(ball, ball.position, {
                x: (dx / dist) * 0.05,
                y: (dy / dist) * 0.05,
            });
        }
    }

    override chronoIn(): void {
        this._chronoPhase = 'in';
        this._chronoInStartTime = performance.now();
        this._chronoActive = true;
        // Clear all balls for fresh start
        for (const ball of this._balls) {
            Matter.Composite.remove(this._engine.world, ball);
        }
        this._balls = [];
        this._lastSpawnedSecond = -1;
    }

    override isChronoActive(): boolean {
        return this._chronoActive;
    }

    static override getConfigSchema(): WatchfaceConfigParam[] {
        return [
            { key: 'gravity', label: 'gravity', min: 0.2, max: 3, step: 0.1, default: 1, decimals: 1 },
            { key: 'ballSize', label: 'ball size', min: 10, max: 80, step: 1, default: 40, decimals: 0 },
            { key: 'restitution', label: 'bounce', min: 0, max: 0.9, step: 0.05, default: 0.36, decimals: 2 },
            { key: 'handWidth', label: 'hand width', min: 8, max: 50, step: 1, default: 36, decimals: 0 },
        ];
    }

    override setConfig(key: string, value: number): void {
        if (key in this._config) {
            (this._config as Record<string, number>)[key] = value;
        }
    }

    override getConfig(): Record<string, number> {
        return { ...this._config };
    }
}
